import { Input, Label } from '@civilgrid/ui/components';
import { type KeyboardEvent, useId, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';
import type { TOCParent } from '~/common/types';
import CloseIconButton from '~/components2/forms/CloseIconButton';
import layerStore from '~/store2/layers';
import GeoreferencedMapsTOC, { filterGeoreferencedMapsBySearch } from './GeoreferencedMapsTOC';
import { GEOREFERENCED_MAPS_PANEL_ID } from './panelIds';
import { handlePanelEscape, usePanelFocus } from './usePanelFocus';

/** Map panel listing only the project's geo-referenced maps, opened from its own button beside Layers. */
export default function GeoreferencedMapsPanel() {
  const { georeferencedMapsTOCGroup } = useSnapshot(layerStore) as typeof layerStore;
  const [searchTerm, setSearchTerm] = useState('');
  const titleId = useId();
  const searchId = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);

  usePanelFocus(GEOREFERENCED_MAPS_PANEL_ID, () => titleRef.current, true);
  const close = () => layerStore.toggleGeoreferencedMapsPanel(false);

  const hasResults = filterGeoreferencedMapsBySearch(georeferencedMapsTOCGroup as TOCParent[], searchTerm).length > 0;

  return (
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: Escape from any control inside closes the dialog
    <div
      id={GEOREFERENCED_MAPS_PANEL_ID}
      role="dialog"
      aria-labelledby={titleId}
      onKeyDown={(e: KeyboardEvent<HTMLElement>) => handlePanelEscape(e, close)}
      data-testid="georeferenced-maps-panel"
      className="pointer-events-auto absolute left-4 top-4 z-20 flex max-h-[90%] w-[26rem] max-w-[80%] flex-col rounded-sm border border-grey-30 bg-white p-1 shadow-lg"
    >
      <div className="flex w-full items-center justify-between p-2">
        <h2 id={titleId} ref={titleRef} tabIndex={-1} className="rounded-sm text-base font-semibold focus-ring">
          Geo-referenced Maps
        </h2>
        <CloseIconButton
          aria-label="Close geo-referenced maps"
          data-testid="georeferenced-maps-panel-close-button"
          onClick={close}
        />
      </div>
      <div className="flex flex-col gap-1 px-2">
        <Label htmlFor={searchId}>Search maps</Label>
        <Input
          id={searchId}
          type="search"
          data-testid="georeferenced-maps-panel-search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div className="my-1 overflow-y-auto pr-2">
        <GeoreferencedMapsTOC searchTerm={searchTerm} />
      </div>
      <div role="status" className="px-2 text-sm text-grey-70">
        {hasResults ? '' : 'No maps match your search.'}
      </div>
    </div>
  );
}
